import styles from "./MemoryCard.module.css";

export interface MemoryCardProps {
  /** Canonical node name (`entity:device-r1d-2612`, `fact:fleet-tz`, ...). */
  name: string;
  /** Node kind, rendered as a badge; omitted when unknown. */
  kind: string | null;
  /** Resolved attribution — `"you"` / `"a teammate"` — or null to omit the
   *  chip (a trace row whose author principal wasn't recorded). */
  attribution: string | null;
  /** The memory's content — the primary line of the card. Null degrades to an
   *  explicit "no content" marker rather than an empty card body. */
  content: string | null;
  /** Evidence recipe, shown collapsed behind an "evidence" summary so it can
   *  no longer be read past as line-noise — expanding shows the full recipe
   *  verbatim. Deliberately uncounted: steps wrap across stored lines, so any
   *  line-derived number misstates a recipe's length. */
  evidence?: string | null;
  /** `source -[REL]-> target` provenance when the memory was reached through
   *  graph expansion. */
  edge?: string | null;
}

/**
 * One remembered memory, rendered for admin legibility from structured fields.
 *
 * The model-facing render of a memory is a single prose line whose evidence
 * rides as an inline `(evidence: ...)` suffix — a shape admins have repeatedly
 * scanned straight past. This card is the same memory built from the captured
 * fields instead: kind badge and attribution chip up front, content as the
 * primary line, evidence behind a plain "evidence" disclosure, and edge
 * provenance when present. It never parses the model-facing text.
 *
 * Used by the knowledge-graph trace's injected and preferences blocks
 * (rendered `TracedCandidate` rows), which map onto these display props,
 * omitting chips their data genuinely lacks.
 */
export function MemoryCard({
  name,
  kind,
  attribution,
  content,
  evidence,
  edge,
}: MemoryCardProps) {
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        {kind !== null && <span className={styles.kindBadge}>{kind}</span>}
        {attribution !== null && (
          <span
            className={styles.attributionChip}
            data-attribution={attribution === "you" ? "you" : "teammate"}
          >
            {attribution}
          </span>
        )}
        <code className={styles.name}>{name}</code>
      </div>
      {content !== null ? (
        <p className={styles.content}>{content}</p>
      ) : (
        <p className={styles.noContent}>(no content recorded)</p>
      )}
      {edge !== null && edge !== undefined && edge !== "" && (
        <div className={styles.edge}>
          via <code>{edge}</code>
        </div>
      )}
      {evidence !== null && evidence !== undefined && evidence !== "" && (
        <details className={styles.evidence}>
          <summary className={styles.evidenceSummary}>evidence</summary>
          <pre className={styles.evidenceBody}>{evidence}</pre>
        </details>
      )}
    </div>
  );
}
